// Users API Module - Enhanced with pagination and filtering
import { CreateUserContactRequest, UserContact } from "@/types";
import { API_ENDPOINTS } from "../../constants/apiEndpoints";
import { PaginationParams, UsersFilterParams } from "../../types/api";
import {
    AddressDto,
    CreateAddressDto,
    UpdateNomineeDto,
    UserDetailsResponse,
} from "../../types/user";
import api from "../axios";
import { ApiResponse, handleApiResponse } from "../handleApiResponse";

// Enhanced fetchAllUsers with pagination and filtering
export async function fetchAllUsers(
  params: PaginationParams & UsersFilterParams = {}
): Promise<ApiResponse<any>> {
  const queryParams = new URLSearchParams();

  // Add pagination parameters
  if (params.page) queryParams.append("page", params.page.toString());
  if (params.limit) queryParams.append("limit", params.limit.toString());
  if (params.sortBy) queryParams.append("sortBy", params.sortBy);
  if (params.sortOrder) queryParams.append("sortOrder", params.sortOrder);

  // Add filter parameters
  if (params.search) queryParams.append("search", params.search);
  if (params.role) queryParams.append("role", params.role);
  if (params.isActive !== undefined)
    queryParams.append("isActive", params.isActive.toString());
  if (params.fields) queryParams.append("fields", params.fields);
  if (params.includeUser !== undefined)
    queryParams.append("includeUser", params.includeUser.toString());
  if ((params as any).uplineSearch)
    queryParams.append("uplineSearch", (params as any).uplineSearch);

  const url = queryParams.toString()
    ? `${API_ENDPOINTS.USERS.BASE}?${queryParams.toString()}`
    : API_ENDPOINTS.USERS.BASE;

  return handleApiResponse(api.get(url));
}

// Legacy function for backward compatibility
export async function fetchAllUsersLegacy(): Promise<
  ApiResponse<UserDetailsResponse[]>
> {
  return handleApiResponse(api.get(API_ENDPOINTS.USERS.BASE));
}

export async function updateUserStatus(
  userId: string,
  isActive: boolean
): Promise<ApiResponse<any>> {
  return handleApiResponse(
    api.patch(API_ENDPOINTS.USERS.STATUS(userId), { isActive })
  );
}

// Promote user to moderator
export async function promoteUser(
  userId: string,
  payload: {
    department?: string;
    reason: string;
    sendNotification?: boolean;
    grantImmediateAccess?: boolean;
  }
): Promise<ApiResponse<any>> {
  return handleApiResponse(
    api.patch(API_ENDPOINTS.USERS.PROMOTE(userId), payload)
  );
}

export async function fetchCurrentUser(): Promise<
  ApiResponse<UserDetailsResponse>
> {
  // First get the current user's id from the standardized API response
  const meRes = await handleApiResponse(api.get(API_ENDPOINTS.AUTH.ME));
  // Extend the type to allow both _id and id
  const userData = meRes.data as Partial<UserDetailsResponse> & { id?: string };
  const userId = userData?._id || userData?.id;
  if (!meRes.success || !userId) {
    return {
      success: false,
      data: null as any,
      message: meRes.message || "User ID not found in /v1/auth/me response",
    };
  }
  // Then fetch user details by id
  return handleApiResponse(api.get(API_ENDPOINTS.USERS.BY_ID(userId)));
}

export async function fetchUserById(
  userId: string
): Promise<ApiResponse<UserDetailsResponse>> {
  return handleApiResponse(api.get(API_ENDPOINTS.USERS.BY_ID(userId)));
}

// Validate referral code and get upline details
export async function validateReferralCode(
  referralCode: string
): Promise<ApiResponse<{ data: { fullName: string; referralCode: string } }>> {
  return handleApiResponse(
    api.get(API_ENDPOINTS.USERS.VALIDATE_REFERRAL(referralCode))
  );
}

// Profile Management
export async function updateUserProfile(
  profileData: any
): Promise<ApiResponse<any>> {
  return handleApiResponse(api.put(API_ENDPOINTS.AUTH.ME, profileData));
}

//#region Contact Management
export async function getUserContact(
  userId: string
): Promise<ApiResponse<UserContact>> {
  return handleApiResponse(api.get(`${API_ENDPOINTS.CONTACTS.BASE}/${userId}`));
}

export async function updateUserContact(
  userId: string,
  data: Partial<UserContact>
): Promise<ApiResponse<UserContact>> {
  return handleApiResponse<UserContact>(
    api.patch(`${API_ENDPOINTS.CONTACTS.BASE}/${userId}`, data)
  );
}

export async function createUserContact(
  userId: string,
  data: { phone?: string; email?: string }
): Promise<ApiResponse<UserContact>> {
  // Use current date for verifiedAt fields, set isVerified false
  // const now = new Date().toISOString();
  const payload: CreateUserContactRequest = {
    userId,
  };
  if (data.phone) payload.phone = data.phone;
  if (data.email) payload.email = data.email;
  return handleApiResponse<UserContact>(
    api.post(API_ENDPOINTS.CONTACTS.BASE, payload)
  );
}
//#endregion

// Address Management
// Note: API returns nested structure { data: [...], pagination: {...} }
export async function fetchAddresses(
  userId: string
): Promise<ApiResponse<{ data: AddressDto[]; pagination?: any }>> {
  return handleApiResponse(
    api.get(API_ENDPOINTS.ADDRESSES.BASE, { params: { userId } })
  );
}

export async function addAddress(
  address: CreateAddressDto
): Promise<ApiResponse<AddressDto>> {
  return handleApiResponse(api.post(API_ENDPOINTS.ADDRESSES.BASE, address));
}

export async function updateAddress(
  id: string,
  address: Partial<AddressDto>
): Promise<ApiResponse<AddressDto>> {
  return handleApiResponse(
    api.patch(API_ENDPOINTS.ADDRESSES.BY_ID(id), address)
  );
}

export async function deleteAddress(id: string): Promise<ApiResponse<void>> {
  return handleApiResponse(api.delete(API_ENDPOINTS.ADDRESSES.BY_ID(id)));
}

// Update nominee details for the current user
export async function updateMyNominee(
  nominee: UpdateNomineeDto
): Promise<ApiResponse<any>> {
  return handleApiResponse(api.patch(API_ENDPOINTS.USERS.MY_NOMINEE, nominee));
}
